import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "../customOauth/logo.svg";

export const oauth2JwtBearer = defineConnector({
  identifier: "oauth2JwtBearer",
  name: "OAuth 2.0 JWT bearer",
  tagLine: "Exchange signed assertions for access tokens",
  logo,
  tags: ["custom"],
  keywords:
    "oauth oauth2 jwt json web token bearer assertion service account rfc 7523",
  protocol: "http",
  defaultUrlPattern: () => ".*",
  customizeStepHandledInline: true,
  auth: (app) =>
    customAuth("jwtBearer", {
      tokenUrl: app.config.tokenUrl,
      issuer: app.config.issuer,
      subject: app.config.subject,
      audience: app.config.audience,
      scope: app.config.scope,
      keyId: app.config.keyId,
      privateKey: app.secret.privateKey,
    }),
  steps: [
    {
      title: "Configure your JWT bearer connection",
      items: [
        {
          kind: "input",
          label: "Name",
          placeholder: "e.g. Acme API",
          defaultValue: "OAuth 2.0 JWT bearer",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "url-pattern",
          placeholder: "api.example.com",
          target: { kind: "connectedApp", field: "urlPattern" },
        },
        {
          kind: "test-url",
          label: "Test URL",
          placeholder: "https://api.example.com/me",
          helperText:
            "Must match the URL pattern. Leave blank to skip testing.",
          urlTarget: { kind: "configuration", field: "testUrl" },
          methodTarget: { kind: "configuration", field: "testMethod" },
        },
        {
          kind: "markdown",
          content:
            "Configure the authorization server to accept the [OAuth 2.0 JWT bearer grant](https://www.rfc-editor.org/rfc/rfc7523.html) and register the public key matching the private key below.",
        },
        {
          kind: "input",
          label: "Token URL",
          placeholder: "https://auth.example.com/oauth/token",
          helperText: "Must use HTTPS. Query parameters are preserved.",
          required: true,
          target: { kind: "configuration", field: "tokenUrl" },
        },
        {
          kind: "input",
          label: "Issuer",
          placeholder: "client@example.com",
          helperText:
            "The JWT’s iss claim, often a client ID or service account.",
          required: true,
          target: { kind: "configuration", field: "issuer" },
        },
        {
          kind: "input",
          label: "Subject",
          placeholder: "client@example.com",
          helperText: "The JWT’s sub claim. It may be the same as the issuer.",
          required: true,
          target: { kind: "configuration", field: "subject" },
        },
        {
          kind: "input",
          label: "Audience",
          placeholder: "https://auth.example.com/oauth/token",
          helperText: "The JWT’s aud claim. Defaults to the token URL.",
          target: { kind: "configuration", field: "audience" },
        },
        {
          kind: "input",
          label: "Scopes",
          placeholder: "read write",
          helperText:
            "Optional, space-separated scopes sent to the token endpoint.",
          target: { kind: "configuration", field: "scope" },
        },
        {
          kind: "input",
          label: "Key ID",
          placeholder: "key-1",
          helperText: "Optional kid value for the JWT header.",
          target: { kind: "configuration", field: "keyId" },
        },
        {
          kind: "file",
          label: "Private key",
          accept: ".pem,.key",
          helperText:
            "An RSA private key in PEM format, used to sign assertions.",
          target: { kind: "secrets", field: "privateKey" },
        },
        {
          kind: "file",
          label: "CA certificate",
          accept: ".pem,.crt,.cer",
          helperText:
            "Optional. Upload a PEM CA certificate to trust a private or self-signed server.",
          target: { kind: "configuration", field: "caCertificate" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ testUrl, testMethod }) => ({
      url: testUrl ?? "",
      method: testMethod === "POST" ? "POST" : "GET",
    }),
  },
  usageHint: () =>
    "This connector signs a short-lived RS256 JWT bearer assertion, exchanges it for an OAuth 2.0 access token, and adds that token to requests automatically.",
});
